import * as React from "react";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";
import { useHistory } from "react-router-dom";

import { Text } from "src/library/components/Text/Text";
import { formatPrice } from "src/core/prices/utils";
import { transactionTypeToHuman } from "src/modules/payment/utils";
import { TransactionStatusText } from "src/modules/payment/components/TransactionStatusText";
import { Button } from "src/library/components/Button";

import { ITransaction } from "../../api/transactions.api";

interface TransactionRowProps {
  transaction: ITransaction;
}

const TransactionRow: React.ReactFCWithChildren<TransactionRowProps> = ({ transaction }) => {
  const history = useHistory();

  return (
    <TransactionRowWrapper>
      <div className="transaction-info">
        <div className="info-row">
          <Text className="value">
            {`${transactionTypeToHuman(transaction.type)} of ${formatPrice(transaction.amount)} on ${moment.utc(transaction.updatedAt).local().format("DD/MM/YYYY")}`}
          </Text>

          <TransactionStatusText
            className="value"
            status={transaction.status}
          />
        </div>

        {transaction.errorReason && (
          <div className="row-error">
            <Text
              className="value"
              color="error"
            >
              {transaction.errorReason.message}
            </Text>
          </div>
        )}
      </div>

      <div className="button-wrapper">
        <Button
          $buttonType="secondary"
          onClick={() => history.push(`orders/${transaction.orderId}`)}
        >
          View order
        </Button>
      </div>
    </TransactionRowWrapper>
  );
};

export default TransactionRow;

const TransactionRowWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    padding: ${theme.spacing.padding.sm};
    border: 2px solid ${theme.colors.lightGrey};
    margin-bottom: ${theme.spacing.margins.sm};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .transaction-info {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
    }

    .button-wrapper {
      min-width: 100px;
    }

    .info-row {
      margin-right: ${theme.spacing.margins.md};
      display: flex;
      flex-direction: column;
    } 

    .total-paid {
      display: block;
      width: 100%;
      text-align: right;
    }

    .transactions {
      max-height: 70vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;

      .transaction {
        margin-top: ${theme.spacing.margins.sm};
        padding-top: ${theme.spacing.margins.sm};
        border-top: 0.1rem solid ${theme.colors.lightestGrey};

        &:first-child {
          margin-top: 0;
          padding-top: 0;
        }

        .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .date,
          .amount {
            font-size: ${theme.fonts.sizes.h4};
            font-weight: ${theme.fonts.weights.bold};
          }
        }

        .transaction-content {
          padding-top: ${theme.spacing.margins.sm};

          .row {
            display: flex;
            width: 100%;
            padding-top: 0.2rem;

            .row-inner {
              width: 45%;

              &.price-wrapper {
                width: 20%
              }
            }

            .price {
              display: block;
              width: 100%;
              text-align: right;
            }

            .type {
              text-transform: capitalize;
            }

            &:first-of-type {
              padding-top: 0;
            }

            .label,
            .value {
              display: block;
            }

            .label {
              padding-right: 0.6rem;
            }

            a.value {
              transition: ${theme.transition.quickest};;
              color: ${theme.colors.primaryDark};

              &:hover {
                color: ${theme.colors.primaryDark};
              }
            }

            &.message, &.error  {
              font-style: italic;
              margin-top: ${theme.spacing.margins.sm};
            }
          }
        }
      }
    }
  `}
`;
